import { useState } from 'react';
import StacksApp from '@zondax/ledger-stacks';
import { useObservable, useSubscription } from 'observable-hooks';
import { catchError, delay, from, map, mergeMap, retry, Subject, tap } from 'rxjs';

import { useLedgerResponseState } from './generic-ledger-utils';

enum LedgerConnectionErrors {
  FailedToConnect = 'FailedToConnect',
  AppNotOpen = 'AppNotOpen',
  AppVersionOutdated = 'AppVersionOutdated',
  DeviceNotConnected = 'DeviceNotConnected',
  DeviceLocked = 'DeviceLocked',
}

interface UseRequestLedgerKeysArgs<App> {
  chain: any;
  isAppOpen(args: any): boolean;
  getAppVersion(app: App): Promise<unknown>;
  connectApp(): Promise<App>;
  pullKeysFromDevice(app: App): Promise<void>;
  onSuccess(): void;
}

export function useRequestLedgerKeys<App extends StacksApp>({
  chain,
  connectApp,
  getAppVersion,
  pullKeysFromDevice,
  isAppOpen,
  onSuccess,
}: UseRequestLedgerKeysArgs<App>) {
  const [outdatedAppVersionWarning, setAppVersionOutdatedWarning] = useState(false);
  const [latestDeviceResponse, setLatestDeviceResponse] = useLedgerResponseState();
  const [awaitingDeviceConnection, setAwaitingDeviceConnection] = useState(false);

  const initAction$ = useObservable(() => new Subject());

  function connectLedgerWithFailState() {
    return from(connectApp()).pipe(
      catchError((e) => {
        setAwaitingDeviceConnection(false);
        throw new Error(LedgerConnectionErrors.FailedToConnect);
      })
    );
  }

  function checkCorrectAppIsOpenWithFailState(app: App) {
    return from(getAppVersion(app)).pipe(
      map((response) => {
        if (!isAppOpen(response)) {
          setAwaitingDeviceConnection(false);
          throw new Error(LedgerConnectionErrors.AppNotOpen);
        }

        return response;
      })
    );
  }

  const pullKeys$ = useObservable(() =>
    initAction$.pipe(
      tap(() => setAwaitingDeviceConnection(true)),
      mergeMap(() => connectLedgerWithFailState()),
      mergeMap((app) =>
        checkCorrectAppIsOpenWithFailState(app).pipe(
          delay(100),
          mergeMap(() => pullKeysFromDevice(app)),
          tap(() => setAwaitingDeviceConnection(false)),
          mergeMap(() => app.transport.close()),
          tap(() => onSuccess?.()),
          catchError((e) => {
            setAwaitingDeviceConnection(false);
            if (
              e.name === 'LockedDeviceError' ||
              e.message.includes('LockedDeviceError') ||
              e.message === LedgerConnectionErrors.DeviceLocked
            ) {
              setLatestDeviceResponse({ deviceLocked: true } as any);
              throw new Error(LedgerConnectionErrors.DeviceLocked);
            }

            return app.transport.close();
          })
        )
      ),
      retry()
    )
  );

  useSubscription(pullKeys$);

  return {
    async requestKeys() {
      initAction$.next(undefined);
    },
    outdatedAppVersionWarning,
    setAppVersionOutdatedWarning,
    latestDeviceResponse,
    setLatestDeviceResponse,
    awaitingDeviceConnection,
    setAwaitingDeviceConnection,
  };
}
